import type { Category } from "~/models/category"
import type { Keyword } from "~/models/keyword"
import type { Post } from "~/models/post"

export const useCmsStore = defineStore("cms", () => {
	const contentBundle = async <T extends readonly ContentBundleQuery<string, keyof ContentBundleDataMap>[]>(
		formData: ContentBundleRequest<T>
	) => {
		const data = await $fetch<ContentBundleResponse<T>>(`cms/content-bundle`, {
			...useFetchConfig(),
			method: "POST",
			body: formData
		})
		return Object.fromEntries(data.data.map((v) => [v.query_name, v.data])) as ContentBundleMappedResponseData<T>
	}

	return {
		contentBundle
	}
})

// post :: content bundle
type ContentBundleQueryMap = {
	post: {
		id?: number
		slug?: number
		view?: boolean
	}
	category: {
		id?: number
		slug?: number
	}
	posts: {
		// list
		search?: string
		page?: number
		limit?: number
		order_by?: string
		direction?: string
		ids?: string
		types?: string
		except_ids?: string
		except_slugs?: string
		// post list
		category_ids?: string
		category_slugs?: string
		user_id?: number
		kinds?: string
		keyword_slugs?: string
		from_price?: number
		to_price?: number
		from_date?: string
		to_date?: string
		has_image?: boolean
		option_groups?: string
		sort_by_num?: boolean
	}
	categories: {
		// list
		search?: string
		page?: number
		limit?: number
		order_by?: string
		direction?: string
		ids?: string
		types?: string
		except_ids?: string
		except_slugs?: string
		// category list
		level?: number
		parent_ids?: string
		parent_slugs?: string
		has_image?: boolean
		sort_by_num?: boolean
	}
}
type ContentBundleDataMap = {
	post: Post
	category: Category
	posts: {
		posts: Post[]
		total: number
		r_keywords: Keyword[]
		r_price_range: [number, number]
		r_option_ids: number[]
		r_category_ids: number[]
	}
	categories: {
		categories: Category[]
		total: number
	}
}
type ContentBundleQuery<N extends string, T extends keyof ContentBundleDataMap> = {
	query_name: N
	query_type: T
	query_active?: boolean
} & ContentBundleQueryMap[T]
type ContentBundleQueryResultOf<N extends string, T extends keyof ContentBundleDataMap> = {
	query_name: N
	query_type: T
	success: boolean
	code: number
	data?: ContentBundleDataMap[T]
	errors?: Record<string, Record<string, string>>
}
type ContentBundleMappedResponseData<T extends readonly ContentBundleQuery<string, keyof ContentBundleDataMap>[]> = {
	[K in T[number]["query_name"]]: Extract<T[number], { query_name: K }> extends ContentBundleQuery<
		K,
		infer Q extends keyof ContentBundleDataMap
	>
		? ContentBundleQueryResultOf<K, Q>["data"]
		: never
}
type ContentBundleRequest<T extends readonly ContentBundleQuery<string, keyof ContentBundleDataMap>[]> = {
	company_id: number | null
	queries: T
}
type ContentBundleResponse<T extends readonly ContentBundleQuery<string, keyof ContentBundleDataMap>[]> = {
	data: {
		[K in keyof T]: T[K] extends ContentBundleQuery<infer N, infer Q extends keyof ContentBundleDataMap>
			? ContentBundleQueryResultOf<N, Q>
			: never
	}
}
